import "moment/locale/he";
import moment from "moment/min/moment-with-locales";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import mastercardLogo from "../../../img/svg/Mastercard_new.svg";
import passArt from "../../../img/svg/multiple-entry-pass-card.svg";
import ImageWithFallback from "../../../shared/imageWithFallback";
import PassPromoBanner from "../../components/PassPromoBanner";
import ScreenHeader from "../../components/ScreenHeader";
import "./purchaseSummary.scss";

const ChevronEnd = () => (
  <svg className="ps-v2-chevron" width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 1L1 7L7 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="ps-v2-meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
    <path d="M3 9H21" stroke="currentColor" strokeWidth="1.8" />
    <path d="M8 3V6M16 3V6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PinIcon = () => (
  <svg className="ps-v2-meta-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 21C12 21 19 15.5 19 10A7 7 0 1 0 5 10C5 15.5 12 21 12 21Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const TicketIcon = () => (
  <svg className="ps-v2-pay-icon ps-v2-pay-icon--ticket" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 9V7A2 2 0 0 1 5 5H19A2 2 0 0 1 21 7V9A3 3 0 0 0 21 15V17A2 2 0 0 1 19 19H5A2 2 0 0 1 3 17V15A3 3 0 0 0 3 9Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14 6V8M14 11V13M14 16V18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="1 3" />
  </svg>
);

const CardIcon = () => (
  <svg className="ps-v2-pay-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="5" width="20" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2 10H22" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

// V2 purchase summary (/summary) — presentational. All payment/order logic stays in the V1
// screens/summary.jsx, which passes state + handlers as props.
function PurchaseSummary({
  type,
  clubName,
  lessonName,
  lessonStartDate,
  lessonEndDate,
  date,
  hours,
  minutes,
  address,
  isOrthodox,
  imgSrc,
  defaultImg,
  imgFallbackSrc,
  hasCard,
  last4Digit,
  total,
  passActive,
  passRemainingEntries,
  passExhausted,
  showPassBanner,
  onPay,
  onPaymentRowClick,
  onPassBannerClick,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Date line per flow type: checkin = none; book = lesson range; invite = picked date + time.
  const dateLine = (() => {
    try {
      if (type === "book" && lessonStartDate) {
        const start = moment(lessonStartDate, "DD.MM.YYYY HH:mm").locale("he");
        const endTime = lessonEndDate?.split(" ")[1];
        return `${start.format("dddd, D MMMM")} ${start.format("HH:mm")}${endTime ? ` - ${endTime}` : ""}`;
      }
      if (type === "invite" && date) {
        return `${moment(date, "DD/MM/YYYY").locale("he").format("dddd, D MMMM")} ${hours}:${minutes}`;
      }
    } catch {
      return null;
    }
    return null;
  })();

  const cardRow = hasCard && (
    <button type="button" className="ps-v2-pay-row ps-v2-pay-row--tappable" onClick={onPaymentRowClick}>
      <img className="ps-v2-mastercard" src={mastercardLogo} alt="" />
      <p className="ps-v2-card-digits">•••• {last4Digit}</p>
      <span className="ps-v2-pay-row-spacer" />
      <ChevronEnd />
    </button>
  );

  return (
    <div className="ps-v2">
      <ScreenHeader title={t("PURCHASE_SUMMARY")} onBack={() => navigate(-1)} />

      <section className="ps-v2-section ps-v2-order-card">
        {/* Image first in DOM = rightmost in RTL, per design */}
        <div className="ps-v2-order-img">
          <ImageWithFallback src={isOrthodox ? defaultImg : imgSrc} fallbackSrc={imgFallbackSrc} alt="Club" />
        </div>
        <div className="ps-v2-order-info">
          <p className="ps-v2-club">{clubName}</p>
          {lessonName && <p className="ps-v2-activity">{lessonName}</p>}
          {dateLine && (
            <p className="ps-v2-meta">
              <CalendarIcon />
              {dateLine}
            </p>
          )}
          {address && (
            <p className="ps-v2-meta">
              <PinIcon />
              {address}
            </p>
          )}
        </div>
      </section>

      <section className="ps-v2-section">
        <p className="ps-v2-section-title">{t("PAYMENT_SECTION")}</p>

        {passActive && (
          <div className="ps-v2-pay-row">
            <TicketIcon />
            <div className="ps-v2-pay-texts">
              <p className="ps-v2-pay-title">{t("PASS_PAYMENT_TITLE")}</p>
              <p className="ps-v2-pay-subtitle">{t("PASS_ENTRIES_LEFT", { count: passRemainingEntries })}</p>
            </div>
          </div>
        )}

        {passActive ? (
          cardRow
        ) : hasCard ? (
          cardRow
        ) : (
          <button type="button" className="ps-v2-pay-row ps-v2-pay-row--tappable" onClick={onPaymentRowClick}>
            <CardIcon />
            <div className="ps-v2-pay-texts">
              <p className="ps-v2-pay-title">{t("PAYMENT_METHOD")}</p>
              <p className="ps-v2-pay-subtitle">{t("ADD_CREDIT_CARD_SUBTITLE")}</p>
            </div>
            <span className="ps-v2-pay-row-spacer" />
            <ChevronEnd />
          </button>
        )}
      </section>

      {/* One continuous white surface from just below the payment section to the bottom;
          the summary + banner content is bottom-aligned inside it, near the CTA. */}
      <div className="ps-v2-bottom-block">
        {passExhausted ? (
          <section className="ps-v2-section ps-v2-order-summary">
            <p className="ps-v2-pass-exhausted-note">{t("PASS_EXHAUSTED_NOTE")}</p>
          </section>
        ) : (
          <section className="ps-v2-section ps-v2-order-summary">
            <p className="ps-v2-section-title">{t("ORDER_SUMMARY")}</p>
            {passActive && (
              <div className="ps-v2-total-row">
                <span>{t("PASS_REDEEM_LABEL")}</span>
                <span>{t("PASS_REDEEM_VALUE")}</span>
              </div>
            )}
            <div className="ps-v2-total-row">
              <span>{t("TOTAL_TO_PAY")}</span>
              <span className="ps-v2-total-value">₪{passActive ? 0 : total}</span>
            </div>
          </section>
        )}

        {showPassBanner && <PassPromoBanner passArt={passArt} onClick={onPassBannerClick} />}
      </div>

      <div className="ps-v2-bottom-bar">
        <button type="button" className="ps-v2-cta" onClick={onPay} disabled={passExhausted}>
          {t("PURCHASE_CTA")}
        </button>
      </div>
    </div>
  );
}

export default PurchaseSummary;
